import { useMemo, useState } from 'react';
import { Plus, Search, DollarSign, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { usePayments } from '@/hooks/useFinancials';
import { PAYMENT_METHOD_LABELS } from '@/types/financials';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { RecordPaymentModal } from '@/components/financials/RecordPaymentModal';
import { formatCurrency } from '@/lib/utils';

export function PaymentsPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'financials', 'write') : false;

  const [search, setSearch] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);

  const { data, isLoading, error } = usePayments();

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    const s = search.toLowerCase();
    return data.filter((p) => {
      const hay = `${p.number} ${p.clientName ?? ''} ${p.reference ?? ''}`.toLowerCase();
      return hay.includes(s);
    });
  }, [data, search]);

  const stats = useMemo(() => {
    if (!data) return { count: 0, total: 0, unapplied: 0 };
    return {
      count: data.length,
      total: data.reduce((s, p) => s + p.amount, 0),
      unapplied: data.reduce((s, p) => s + p.unapplied, 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load payments"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Payments" value={String(stats.count)} />
        <Stat label="Total Received" value={stats.total ? formatCurrency(stats.total) : '—'} accent />
        <Stat label="Unapplied" value={stats.unapplied ? formatCurrency(stats.unapplied) : '—'} alert={stats.unapplied > 0} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by payment #, client, or reference…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setRecordOpen(true)}>
            Record Payment
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={DollarSign}
            title={search ? 'No payments match your search' : 'No payments yet'}
            description={search ? 'Try a different search.' : 'Record your first payment when cash comes in.'}
            action={canWrite && !search && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setRecordOpen(true)}>
                Record Payment
              </Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Payment</th>
                <th className="kicker px-6 py-3 font-normal">Client</th>
                <th className="kicker px-6 py-3 font-normal">Received</th>
                <th className="kicker px-6 py-3 font-normal">Method / Ref</th>
                <th className="kicker px-6 py-3 font-normal text-right">Amount</th>
                <th className="kicker px-6 py-3 font-normal text-right">Applied</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const applied = p.applications.reduce((s, a) => s + a.amount, 0);
                const isFullyApplied = p.unapplied < 0.01;
                return (
                  <tr key={p.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                    <td className="px-6 py-4 font-mono text-brass-300">{p.number}</td>
                    <td className="px-6 py-4 text-text">{p.clientName ?? <span className="text-text-dim">—</span>}</td>
                    <td className="px-6 py-4 text-text-muted tabular text-xs">
                      {new Date(p.receivedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text">{PAYMENT_METHOD_LABELS[p.method]}</div>
                      {p.reference && <div className="text-2xs text-text-dim font-mono">{p.reference}</div>}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text font-medium">
                      {formatCurrency(p.amount)}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text-muted">
                      {applied > 0 ? formatCurrency(applied) : <span className="text-text-dim">—</span>}
                      <div className="text-2xs text-text-dim">{p.applications.length} invoice{p.applications.length === 1 ? '' : 's'}</div>
                    </td>
                    <td className="px-6 py-4">
                      {isFullyApplied
                        ? <Badge tone="success">Applied</Badge>
                        : <Badge tone="warning">{formatCurrency(p.unapplied)} unapplied</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <RecordPaymentModal open={recordOpen} onClose={() => setRecordOpen(false)} />
    </div>
  );
}

function Stat({ label, value, accent, alert }: { label: string; value: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
